import moment from "moment-timezone";
import { signHmacSha256 } from "utils/hmac";

const MEMBERMRN_BASE_URL = process.env.REACT_APP_BASEURL;
const HMAC_SECRET_KEY = process.env.REACT_APP_HMAC_SECRET_KEY;

// `cooperateId` is the funding source's Mongo ObjectId — the same value
// that shows up as :fundingsourceId in the app's routes.
async function buildAuthHeaders(cooperateId, body = {}) {
  const signature = await signHmacSha256(HMAC_SECRET_KEY, body);
  return {
    "x-hmac-signature": signature,
    "x-cooperate-id": cooperateId,
  };
}

// Fetches active (non-cancelled, non-archived) trips for a member by MRN or
// phone number. `searchBy` is "mrn" or "phoneNumber" and selects which query
// param `value` is sent as. `startDate`/`endDate` ("YYYY-MM-DD") optionally
// scope results to trips scheduled within that range. Resolves to null when
// no member exists for that value (404), or { trips, total, timeZone, client }
// when the member exists, or throws otherwise. `timeZone` is the member's
// local IANA zone (e.g. "America/Los_Angeles") and `client` is the member's
// profile — both are present even when `trips` is empty, and are needed when
// creating new trips for the same member.
export async function searchTripsByMrn(
  value,
  cooperateId,
  searchBy = "search",
  { startDate, endDate } = {}
) {
  const headers = await buildAuthHeaders(cooperateId);

  const params = new URLSearchParams({ [searchBy]: value });
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  // URLSearchParams percent-encodes ":" as %3A, which is valid but makes the
  // ISO timestamps unreadable in logs/devtools. Colons don't need escaping
  // inside a query value (no "&"/"="/"?" conflict), so decode them back for
  // a cleaner URL without changing what's actually sent.
  const queryString = params.toString().replace(/%3A/g, ":");

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}memberlink/trips?${queryString}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }
  );

  if (response.status === 404) return null;

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to search trips by MRN");
  }

  return {
    trips: data?.trips ?? [],
    total: data?.total ?? 0,
    timeZone: data?.timeZone ?? null,
    client: data?.client ?? null,
  };
}

// Fetches a member's confirmed trips from GET /membermrn/trips, looked up by
// MRN or phone number with the same params as `searchTripsByMrn`. Resolves to
// { trips, total }.
export async function searchConfirmedTripsByMrn(
  value,
  cooperateId,
  searchBy = "search",
  { startDate, endDate } = {}
) {
  const headers = await buildAuthHeaders(cooperateId);

  const params = new URLSearchParams({ [searchBy]: value });
  if (startDate) params.set("startDate", startDate);
  if (endDate) params.set("endDate", endDate);
  const queryString = params.toString().replace(/%3A/g, ":");

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}membermrn/trips?${queryString}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }
  );

  if (response.status === 404) return { trips: [], total: 0 };

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to load confirmed trips");
  }

  return {
    trips: data?.trips ?? [],
    total: data?.total ?? 0,
  };
}

// Fetches a member's trip stats, looked up by MRN or phone number exactly like
// `searchTripsByMrn`. Resolves to { total, confirmed, unconfirmed,
// trip_intake, used, remaining, limitReached, eligibility }, where
// `eligibility` is { status, startDate, endDate, tripQuota }.
export async function fetchTripStats(value, cooperateId, searchBy = "search") {
  const headers = await buildAuthHeaders(cooperateId);
  const params = new URLSearchParams({ [searchBy]: value });

  const response = await fetch(`${MEMBERMRN_BASE_URL}memberlink/trips/count?${params}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to fetch trip stats");
  }

  return data ?? {};
}

// Fetches the funding source's own profile (e.g. its display name) for the
// corporate account identified by `cooperateId`.
export async function fetchCooperateDetails(cooperateId) {
  const headers = await buildAuthHeaders(cooperateId);

  const response = await fetch(`${MEMBERMRN_BASE_URL}membermrn/cooperate`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to fetch funding source details");
  }

  return data;
}

// Fetches the service areas, levels of service, and space types configured
// for the corporate account, used to populate the Create Trip selectors.
export async function fetchConfigurations(cooperateId) {
  const headers = await buildAuthHeaders(cooperateId);

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}membermrn/configurations`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to fetch configurations");
  }

  return {
    serviceAreas: data?.serviceareas ?? [],
    levelOfServices: data?.levelofservices ?? [],
    spaceTypes: data?.spacetypes ?? [],
  };
}

// Creates a new member profile by MRN. `payload.mrn` is required; the rest
// (including homeAddress) is optional but recommended by the API. Throws
// with the API's message on failure — e.g. 400 when the MRN already exists.
export async function createMemberByMrn(payload, cooperateId) {
  const headers = await buildAuthHeaders(cooperateId, payload);

  const response = await fetch(`${MEMBERMRN_BASE_URL}membermrn`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to create member");
  }

  return data;
}

// Creates (assigns) a new trip for a member.
export async function createTrip(payload, cooperateId) {
  const headers = await buildAuthHeaders(cooperateId, payload);

  const response = await fetch(`${MEMBERMRN_BASE_URL}memberlink/assigns`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to create trip");
  }

  return data;
}

// Cancels an existing trip. `assignId` is the trip's `_id`.
export async function cancelTrip(assignId, cooperateId) {
  const payload = {
    status: "cancelled",
    jobStatus: "cancelled",
    changeReason: "Cancelled by member via Link",
    source: "Member Link",
  };
  const headers = await buildAuthHeaders(cooperateId, payload);

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}membermrn/assigns/${assignId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to Cancel");
  }

  return data;
}

// Activates a "Will Call" trip once dispatch has an actual pickup time from
// the facility. Only the activation fields are sent, not the whole trip
// record. `assignId` is the trip's `_id`. `preScheduleTime` is the actual
// pickup time just entered (ISO string) — it's also sent as `scheduleTime`
// so the trip's displayed pickup time updates immediately instead of
// staying pinned at the 23:59 Will Call placeholder. The dropoff/appointment
// time isn't known either while the trip is Will Call (it's also pinned to
// 23:59), so it's derived here as one hour after the chosen pickup time and
// sent as both `preAppointmentTime` and `appointmentTime`.
export async function activateWillCall(
  assignId,
  cooperateId,
  { preScheduleTime, companyNote } = {}
) {
  const appointmentTime = preScheduleTime
    ? moment(preScheduleTime).add(1, "hour").toISOString()
    : null;

  const payload = {
    isWillCallActivated: true,
    source: "Member Link",
    preScheduleTime,
    scheduleTime: preScheduleTime,
    preAppointmentTime: appointmentTime,
    appointmentTime,
    companyNote,
  };
  const headers = await buildAuthHeaders(cooperateId, payload);

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}memberlink/assigns/${assignId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to activate Will Call trip");
  }

  return data;
}

// Updates a trip's intake status (e.g. "confirmed" or "unconfirmed").
// `assignId` is the trip's `_id`.
export async function updateIntakeStatus(assignId, cooperateId, intakeStatus) {
  const payload = {
    _id: assignId,
    intakeStatus,
  };
  const headers = await buildAuthHeaders(cooperateId, payload);

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}memberlink/assigns/${assignId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to update intake status");
  }

  return data;
}

// Proposes/updates a suggested intake pickup time for a trip.
// Sets `intakeStatus` to "suggested_plan" alongside `intakeSuggestedTime`.
export async function suggestIntakeTime(
  assignId,
  cooperateId,
  intakeSuggestedTime
) {
  const payload = {
    _id: assignId,
    intakeStatus: "suggested_plan",
    preIntakeSuggestedTime: intakeSuggestedTime,
    intakeSuggestedTime,
  };
  const headers = await buildAuthHeaders(cooperateId, payload);

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}memberlink/assigns/${assignId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: JSON.stringify(payload),
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to update suggested time");
  }

  return data;
}

// Fetches the full detail record for a single trip/assignment, used to
// enrich the trip details modal with data the trips list search doesn't
// return (e.g. the linked client's own gender). `assignId` is the trip's
// `_id`.
export async function fetchTripDetail(assignId, cooperateId) {
  const headers = await buildAuthHeaders(cooperateId);

  const response = await fetch(
    `${MEMBERMRN_BASE_URL}memberlink/assigns/${assignId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    }
  );

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to fetch trip details");
  }

  return data;
}

// Fetches the driver's current ETA message for a trip in progress.
// `assignId` is the trip's `_id`.
export async function fetchTripEta(assignId, cooperateId) {
  const headers = await buildAuthHeaders(cooperateId);

  const response = await fetch(`${MEMBERMRN_BASE_URL}membermrn/eta/${assignId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || "Unable to fetch ETA");
  }

  return data?.etaMessage ?? null;
}
